import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import useScrollToError from "../../../../../../shared/hooks/useScrollToError";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams, useNavigate } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { ActionButtonConfig } from "../../../../../../shared/components/modals/action-popup";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { filterRmsCodesByStandard } from "../../../../../../shared/app-configs/rms-standard-mapping";
import { 
  IRawMaterialDetails, 
  IRawMaterialDetailsApiResponse, 
  mapRawMaterialDetailsPayload,
  initialRawMaterialDetails,
  mapRawMaterialDetailsApiResponseToInitialValues
 } from "../../../sc-models/raw-material-details-dto";
import {
  buildRawMaterialValidationSchema,
  validateRawMaterialRow,
} from "../../utils/scValidation";

interface RawMaterialDetailsTabProps {
  onNextTab?: () => Promise<void>;
  scStatus?: boolean;
}

const RawMaterialDetailsTab: React.FC<RawMaterialDetailsTabProps> = ({ onNextTab, scStatus }) => {
  const { formtype, SC_ID } = useParams();
  const navigate = useNavigate();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;
  const [data, setData] = useState<IRawMaterialDetails[]>([]);
  const [tableData, setTableData] = useState<IRawMaterialDetails[]>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [deletingRowIndex, setDeletingRowIndex] = useState<number>(-1);
  const [rawMaterialCodeOptions, setRawMaterialCodeOptions] = useState<OptionType[]>([]);
  const [allRawMaterialCodeOptions, setAllRawMaterialCodeOptions] = useState<OptionType[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const { notify } = useNotification();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();
  const { executePost,  loading: isSaving } = useApi();
  const { executeGet,executeDelete, loading: isLoading } = useApi();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");

  // Strip trailing " (CODE)" from a label e.g. "Organic Cotton (RM0104)" → "Organic Cotton"
  const stripCode = (label: string) => label.replace(/\s*\([^)]+\)\s*$/, '').trim();

  // Fetch selected standards from the database
  const getSelectedStandards = async () => {
    if (!SC_ID) return [];
    try {
      const response = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
      if (response?.status === 200 && response.data) {
        const standards = response.data.scStandardInformation?.map(
          (item: any) => item.scStandard
        ) || [];
        setSelectedStandards(standards);
        return standards;
      }
    } catch (error) {
      console.error('Error fetching selected standards:', error);
    }
    return [];
  };

  // Fetch raw material codes from API
  const getRawMaterialCodes = async (standardsList?: string[]) => {
    try {
      const result: any = await dtrackItServiceV4.getRawMaterialCodes();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        code: t.code_1,
      }));
      
      // Remove duplicates based on value
      const uniqueOptions = options.filter((option: any, index: number, self: any[]) => 
        index === self.findIndex(o => o.value === option.value)
      );
      
      setAllRawMaterialCodeOptions(uniqueOptions);

      // Filter options immediately
      const activeStandards = standardsList ?? selectedStandards;
      const filtered = filterRmsCodesByStandard(activeStandards, uniqueOptions as Array<{ value: string; label: string }>);
      setRawMaterialCodeOptions(filtered);
    } catch (err) {
      Utills.cl(`Error in Fetching Raw Material Codes ${err}`);
      notify('Error fetching raw material codes', 'error');
    }
  };

  // Reactive filtering when master codes or selected standards change
  useEffect(() => {
    if (allRawMaterialCodeOptions.length === 0) return;
    const filtered = filterRmsCodesByStandard(selectedStandards, allRawMaterialCodeOptions as Array<{ value: string; label: string }>);
    setRawMaterialCodeOptions(filtered);
  }, [allRawMaterialCodeOptions, selectedStandards]);

  // Fetch existing raw material details from API
  const getRawMaterialDetails = async () => {
    if (!SC_ID) return;
    
    try {
      const response = await executeGet(API_ROUTES.GET_RAW_MATERIAL_DETAILS_V4, { scId: SC_ID });
      
      if (response?.status === 200 && response.data) {
        const apiData: IRawMaterialDetailsApiResponse = response.data;
        
        if (apiData.response && apiData.response.length > 0) {
          // Transform API response to component data format
          const transformedData: IRawMaterialDetails[] = apiData.response.map(item => ({
            rawMaterialId: item.rawMaterialId,
            scProductRawMaterialCode: item.scProductRawMaterialCode || "",
            scProductRawMaterialCodeDescription: stripCode(item.scProductRawMaterialCodeDescription || ""),
            scProductRawMaterialUserSpecificTerm: item.scProductRawMaterialUserSpecificTerm || ""
          }));
          
          setData(transformedData);
          setTableData(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching raw material details:', error);
      notify('Error fetching raw material details', 'error');
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsInitialLoading(true);
      try {
        const standards = await getSelectedStandards();
        await getRawMaterialCodes(standards);
        await getRawMaterialDetails();
      } finally {
        setIsInitialLoading(false);
      }
    };
    init();
  }, [SC_ID]);

  const formik = useFormik<IRawMaterialDetails>({
    initialValues: initialRawMaterialDetails,
    validationSchema: buildRawMaterialValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  useScrollToError(formik);

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    console.log("columnId :: ", columnId);
    console.log("value :: ", value);

    if(columnId === 'scProductRawMaterialCode') {
      let optionCode = rawMaterialCodeOptions.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scProductRawMaterialCode', value);
      // Set the description field when dropdown value changes
      const selectedOption = rawMaterialCodeOptions.find(option => option.value === value);
      if (selectedOption) {
        const description = stripCode(selectedOption.label);
        formik.setFieldValue('scProductRawMaterialCodeDescription', description);
        
        // Auto-fill User Specific Term with "na" if code is not RM0262 (Others)
        const userSpecificTerm = '';
        formik.setFieldValue('scProductRawMaterialUserSpecificTerm', userSpecificTerm);
        
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { 
              ...row, 
              [columnId]: value, 
              scProductRawMaterialCodeDescription: description,
              scProductRawMaterialUserSpecificTerm: userSpecificTerm
            } : row
          )
        );
      } else {
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row
          )
        );
      }
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: IRawMaterialDetails = initialRawMaterialDetails;
    setRestrictPopUpSave(true);
    setData((prevData) => [newRow, ...prevData]);
    setEditingRowIndex(0);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null) {
      notify('No row selected for editing', 'error');
      return;
    }

    const currentRowData = data[editingRowIndex];
    const { fieldErrors, generalError } = validateRawMaterialRow(
      currentRowData,
      data,
      editingRowIndex
    );

    if (Object.keys(fieldErrors).length > 0) {
      const touchedMap: Record<string, boolean> = {};
      Object.entries(fieldErrors).forEach(([field, message]) => {
        formik.setFieldError(field, message);
        touchedMap[field] = true;
      });
      formik.setTouched(touchedMap);
      notify("Please fix the highlighted errors", "error");
      formik.submitForm(); // Trigger scroll hook
      return;
    }

    if (generalError) {
      notify(generalError, 'error');
      return;
    }

    try {
      // Use the current row data instead of formik values to ensure we have the latest data
      const updatedRow = currentRowData;
      
      // Get the existing row data to check if it has rawMaterialId
      const existingRow = data[editingRowIndex];
      
      // Call the API to save the raw material details
      const payload = mapRawMaterialDetailsPayload(updatedRow, SC_ID!);
      if (existingRow.rawMaterialId) {
        payload.rawMaterialId = existingRow.rawMaterialId; // Include rawMaterialId if editing existing row
      }

      const response = await executePost(API_ROUTES.POST_RAW_MATERIAL_DETAILS_V4, payload);
      
      if (response?.status === 200 || response?.status === 201) {
        // Update the row with the new data while preserving the rawMaterialId
        const newData = data.map((row, index) =>
            index === editingRowIndex ? { ...updatedRow, rawMaterialId: existingRow.rawMaterialId } : row
          );
        setData(newData);
        setTableData(newData);

        setRestrictPopUpSave(false);
        setEditingRowIndex(-1);
        formik.resetForm();
        
        // Refresh data from API to ensure it stays updated
        await getRawMaterialDetails();
        
        notify('Raw material details saved successfully', 'success');
        
        // Call onNextTab after successful POST
        if (onNextTab) {
          await onNextTab();
        }
      } else {
        notify('Error saving raw material details', 'error');
      }
    } catch (error) {
      console.error('Error saving raw material details:', error);
      notify('Error saving raw material details', 'error');
    }
  };

  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/component-details/${SC_ID}`);
  };

  const handleNext = async () => {
    if (data.length === 0) {
      notify('Please add at least one Raw Material Detail', 'error');
      return;
    }
    if (onNextTab) {
      await onNextTab();
    }
    handleNavigateToNextTab();
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  const columns: EditableColumnDef<IRawMaterialDetails>[] = [
    {
      accessorKey: "scProductRawMaterialCode",
      header: "SC Product Raw Material Code",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Product Raw Material Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scProductRawMaterialCode"
          value={value}
          placeholder="Select Product Raw Material Code"
          options={rawMaterialCodeOptions}
          enableSearch={true}
          size="small"
          tooltipError={true}
          error={formik.touched.scProductRawMaterialCode && Boolean(formik.errors.scProductRawMaterialCode)}
          helperText={formik.touched.scProductRawMaterialCode && formik.errors.scProductRawMaterialCode ? (formik.errors.scProductRawMaterialCode as string) : undefined}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "scProductRawMaterialCodeDescription",
      header: "Details",
      Header: <span>Details</span>,
    },
    {
      accessorKey: "scProductRawMaterialUserSpecificTerm",
      header: "SC Product Raw Material User Specific Term",
      Header: (
        <span>
          {" "}
          SC Product Raw Material User Specific Term
        </span>
      ),
      size: 240,
      minSize: 150,
      maxSize: 300,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scProductRawMaterialUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product Raw Material User Specific Term"
          tooltipError={true}
          error={formik.touched.scProductRawMaterialUserSpecificTerm && Boolean(formik.errors.scProductRawMaterialUserSpecificTerm)}
          helperText={formik.touched.scProductRawMaterialUserSpecificTerm && formik.errors.scProductRawMaterialUserSpecificTerm ? (formik.errors.scProductRawMaterialUserSpecificTerm as string) : undefined}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik including description
    const rowData = data[rowIndex];
    const formikValues = {
      scProductRawMaterialCode: rowData.scProductRawMaterialCode || "",
      scProductRawMaterialCodeDescription: rowData.scProductRawMaterialCodeDescription || "",
      scProductRawMaterialUserSpecificTerm: rowData.scProductRawMaterialUserSpecificTerm || "",
    };
    
    formik.setValues(formikValues);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const rowToDelete = data[rowIndex];
    
    // Check if the row has a rawMaterialId (exists in database)
    if (rowToDelete.rawMaterialId) {
      // Show confirmation popup for API deletion
      setPopUpTitle("Confirm Delete");
      setPopUpMessage("Are you sure you want to delete this raw material detail? This action cannot be undone.");
      setPopUpBtnName("Delete");
      setDialogOpen(true);
      // Store the row index for deletion after confirmation
      setDeletingRowIndex(rowIndex);
    } else {
      // Delete locally if no rawMaterialId (new row not yet saved)
      const updatedData = data.filter((_, index) => index !== rowIndex);
      setData(updatedData);
      setTableData(updatedData);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const rowIndex = deletingRowIndex;
      const rowToDelete = data[rowIndex];
      
      if (rowToDelete.rawMaterialId) {
        // Call API to delete from database
        const response = await executeDelete(
          API_ROUTES.DELETE_API_V4,
          {
            scId: SC_ID,
            rawMaterialId: rowToDelete.rawMaterialId
          }
        );
        
        if (response?.status === 200) {
          // Remove from local state after successful API deletion
          const updatedData = data.filter((_, index) => index !== rowIndex);
          setData(updatedData);
          setTableData(updatedData);
          
          // Refresh data from API to ensure it stays updated
          await getRawMaterialDetails();
          
          notify('Raw material detail deleted successfully', 'success');
          
          // Call onNextTab after successful DELETE
          if (onNextTab) {
            await onNextTab();
          }
        } else {
          notify('Failed to delete raw material detail', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting raw material detail:', error);
      notify('Error deleting raw material detail', 'error');
    } finally {
      // Reset states
      setDialogOpen(false);
      setDeletingRowIndex(-1);
    }
  };

  const actions: ActionButtonConfig[] = [
    {
      label: "Home",
      onClick: () => {
        navigate('/v4/sc-dashboard');
      },
      variant: "contained",
      color: "default",
    },
    {
      label: "Back",
      onClick: () => {
        navigate(`/v4/sc/${formtype}/product-detail-information/${SC_ID}`);
      },
      variant: "contained" as const,
      color: "default" as const,
    },
    {
      label: "Next",
      onClick: handleNext,
      disabled: isRestrictPopUpSave || isSaving,
      variant: "contained",
      color: "primary",
    },
  ];

  return (
      <>
      
     
 { isInitialLoading ? (
    <LoaderComponent />
  ) : (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || isReadOnlyBasedOnStatus || isRestrictPopUpSave}>
            Add Raw Material Information
          </StyledButton>
        </Box>
        
          <EditableTable
            data={data}
            columns={columns}
            onSaveRow={handleSaveRow}
            onCellChange={handleCellChange}
            editingRowIndex={editingRowIndex}
            onEditRow={handleEditRow}
            onCancelEdit={handleCancelEdit}
            onDeleteRow={handleDeleteRow}
            enableEditing={!isReadOnlyBasedOnStatus}
            isSaving={isSaving}
          />
        
      </Box>

      <div style={{ marginTop: "3rem" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          {actions.map((action, index) => (
            <StyledButton
              key={index}
              onClick={action.onClick}
              variant={action.variant || "text"}
              disabled={action.disabled}
            >
              {action.label}
            </StyledButton>
          ))}
        </Box>
      </div>

      <AlertPopup
        open={dialogOpen}
        title={popUpTitle}
        message={popUpMessage}
        onClose={handleCancelSave}
        onConfirm={handleConfirmDelete}
        confirmText={popUpBtnName}
        cancelText="Cancel"
      />
    </div>)}
    </>
  );
};

export default RawMaterialDetailsTab;
